import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, EMPTY, filter, finalize, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import { CustomerService } from '@app/pages/customers/services/customers.service';
import {
  Customer,
  CustomerDeleteModalRes,
  CustomerPaginatedResponse,
  CustomerQueryParams,
} from '@app/utils/interfaces/customer.interface';
import { CreateCustomerDialogComponent } from '@app/pages/customers/create-customer-dialog/create-customer-dialog.component';
import {
  CUSTOMER_DIALOG_OPTIONS,
  CUSTOMER_TABLE_COLUMNS_CONFIG,
  FAKE_CUSTOMERS_QUANTITY,
} from '@app/utils/constants/customers.constants';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';
import { ViewCustomerDialogComponent } from '@app/pages/customers/view-customer-dialog/view-customer-dialog.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { IconComponent } from '../icon/icon.component';
import { StatusModalComponent } from '../status-modal/status-modal.component';
import { CustomerColumnKey } from '@app/utils/enums/customer-table-columns.enum';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { CustomerEditFormComponent } from '@app/pages/customers/components/customer-edit-form/customer-edit-form.component';
import { REGEX } from '@app/utils/constants/regex.constants';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-customers-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TablePaginationComponent,
    SearchFieldComponent,
    MatTooltipModule,
    IconComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './customers-table.component.html',
  styleUrls: ['./customers-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomersTableComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly customerService = inject(CustomerService);
  private readonly dialog = inject(MatDialog);
  public readonly iconName = IconName;

  public readonly customerColumnKey = CustomerColumnKey;
  public readonly skeletonRows = signal<Partial<Customer>[]>(
    Array(FAKE_CUSTOMERS_QUANTITY).fill({}),
  );
  public customers = signal<Customer[]>([]);

  public customersTableColumnsConfig = signal(CUSTOMER_TABLE_COLUMNS_CONFIG);
  public customersTableColumnKeys = CUSTOMER_TABLE_COLUMNS_CONFIG.map((c) => c.key);
  public customersLoading = signal(false);
  public customersPagination = signal<TablePaginationConfig>(TABLE_PAGINATION_DEFAULT_CONFIG);

  public readonly showPagination = computed(() => Boolean(this.customersPagination().totalItems));

  protected parentSearchControl = new FormControl();

  public ngOnInit(): void {
    this.fetchData();
    this.setupSearchObserve();
  }

  public setupSearchObserve(): void {
    this.parentSearchControl.valueChanges
      .pipe(
        tap((term: string) => {
          this.customersLoading.set(!!term);
        }),
        switchMap((searchTerm) => {
          if (!searchTerm) {
            this.updatePagination({ currentPage: 1 });
            return EMPTY;
          }

          return this.customerService
            .getCustomers({
              pageNumber: this.customersPagination().currentPage,
              pageSize: this.customersPagination().pageSize,
              ...this.getParamsForSearch(searchTerm),
            })
            .pipe(
              tap((res: CustomerPaginatedResponse) => {
                this.customers.set(res.data.items);
                this.customersPagination.update((p) => ({
                  ...p,
                  totalItems: res.data.totalAmount,
                }));
              }),
              catchError(() => {
                this.handleOpenErrorModal();
                this.customers.set([]);
                return EMPTY;
              }),
              finalize(() => {
                this.customersLoading.set(false);
              }),
              takeUntilDestroyed(this.destroyRef),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public handleSearchInput(searchTerm: string): void {
    this.customerService
      .getCustomers({
        pageNumber: this.customersPagination().currentPage,
        pageSize: this.customersPagination().pageSize,
        ...this.getParamsForSearch(searchTerm),
      })
      .pipe(
        tap((res: CustomerPaginatedResponse) => {
          this.customers.set(res.data.items);
          this.customersPagination.update((p) => ({
            ...p,
            totalItems: res.data.totalAmount,
          }));
        }),
        catchError(() => {
          this.handleOpenErrorModal();
          this.customers.set([]);
          return EMPTY;
        }),
        finalize(() => {
          this.customersLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  private fetchData(): void {
    this.customersLoading.set(true);

    const { currentPage, pageSize } = this.customersPagination();

    this.customerService
      .getCustomers({
        PageNumber: currentPage,
        PageSize: pageSize,
      })
      .pipe(
        tap((res: CustomerPaginatedResponse) => {
          this.customers.set(res.data.items);
          this.customersPagination.update((p) => ({
            ...p,
            totalItems: res.data.totalAmount,
          }));
        }),
        catchError(() => {
          this.handleOpenErrorModal();
          this.customers.set([]);
          return EMPTY;
        }),
        finalize(() => {
          this.customersLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public handlePageChange(page: number): void {
    this.updatePagination({ currentPage: page });
  }

  public handlePageSizeChange(pageSize: number): void {
    this.updatePagination({ pageSize, currentPage: 1 });
  }

  public openCreateCustomerDialog() {
    const dialogRef = this.dialog.open(CreateCustomerDialogComponent, CUSTOMER_DIALOG_OPTIONS);

    dialogRef
      .afterClosed()
      .pipe(
        filter((res) => Boolean(res)),
        tap(() => this.updatePagination({ currentPage: 1 })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public openEditCustomerDialog(customer: Customer, menuTrigger: MatMenuTrigger): void {
    menuTrigger.closeMenu();
    const dialogRef = this.dialog.open(CustomerEditFormComponent, {
      ...CUSTOMER_DIALOG_OPTIONS,
      data: customer,
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((res) => Boolean(res)),
        tap((res: CustomerDeleteModalRes) => {
          if (res.action === 'deleteClicked') {
            this.handleDeleteConfirmation(customer, menuTrigger);
            return;
          }
          this.updatePagination({ currentPage: 1 });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public openViewCustomerDialog(customer: Customer): void {
    this.dialog.open(ViewCustomerDialogComponent, {
      ...CUSTOMER_DIALOG_OPTIONS,
      data: { loyaltyId: customer.loyaltyId, email: customer.email },
      autoFocus: false,
      restoreFocus: false,
    });
  }

  private updatePagination(paginationConfig: Partial<TablePaginationConfig>): void {
    this.customersPagination.update((prevState) => ({
      ...prevState,
      ...paginationConfig,
    }));

    this.fetchData();
  }

  private handleOpenErrorModal(
    title = 'customers.table.errors.title',
    description = 'customers.table.errors.customersLoadFail',
  ): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        success: false,
        title,
        description,
      },
    });
  }

  private getParamsForSearch(searchValue: string): CustomerQueryParams {
    return {
      email: new RegExp(REGEX.email).test(searchValue) ? searchValue : undefined,
      loyaltyId: new RegExp(REGEX.loyaltyId).test(searchValue) ? searchValue : undefined,
      search: searchValue,
    };
  }

  private handleDeleteConfirmation(customer: Customer, menuTrigger: MatMenuTrigger): void {
    const deleteConfirmModal = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'customers.table.confirm.delete-title',
        desc: 'customers.table.confirm.delete-desc',
      },
    });

    deleteConfirmModal
      .afterClosed()
      .pipe(
        tap((res) => {
          if (res) {
            this.customersLoading.set(true);
            this.customerService
              .deleteCustomer(customer.accountId)
              .pipe(
                catchError((err) => {
                  this.handleOpenErrorModal(
                    'customers.table.errors.delete-fail',
                    err.error.message,
                  );
                  return EMPTY;
                }),
                finalize(() => {
                  this.customersLoading.set(false);
                }),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe();
          } else {
            this.openEditCustomerDialog(customer, menuTrigger);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
